import { ref, computed } from 'vue'
import { useIntersectionObserver, type MaybeComputedElementRef } from '@vueuse/core'

export function useNavigationScroll() {
    const navigationWrapper = ref<HTMLElement | null>(null)
    const navigationList = ref<HTMLElement | null>(null)
    const sentinelStart = ref<HTMLElement | null>(null)
    const sentinelEnd = ref<HTMLElement | null>(null)

    const sentinelStartVisible = ref(true)
    const sentinelEndVisible = ref(true)

    // Computed properties for arrow visibility based on sentinel visibility
    const showLeftArrow = computed(() => !sentinelStartVisible.value)
    const showRightArrow = computed(() => !sentinelEndVisible.value)

    useIntersectionObserver(
        sentinelStart,
        ([entry]) => {
            sentinelStartVisible.value = !!entry?.isIntersecting
        },
        {
            root: navigationWrapper as MaybeComputedElementRef,
            threshold: 0
        }
    )

    useIntersectionObserver(
        sentinelEnd,
        ([entry]) => {
            sentinelEndVisible.value = !!entry?.isIntersecting
        },
        {
            root: navigationWrapper as MaybeComputedElementRef,
            threshold: 0
        }
    )

    // Scroll to next/previous visible item using native scrollIntoView
    const handleScrollLeft = () => {
        if (!navigationWrapper.value || !navigationList.value) return

        const navRect = navigationWrapper.value.getBoundingClientRect()
        const links = Array.from(navigationList.value.querySelectorAll<HTMLAnchorElement>('a'))

        // Find last link that's cut off on the left
        for (let i = links.length - 1; i >= 0; i--) {
            const link = links[i]
            if (!link) continue

            const linkRect = link.getBoundingClientRect()
            if (linkRect.left < navRect.left - 1) {
                link.scrollIntoView({ behavior: 'smooth', inline: 'end', block: 'nearest' })
                break
            }
        }
    }

    const handleScrollRight = () => {
        if (!navigationWrapper.value || !navigationList.value) return

        const navRect = navigationWrapper.value.getBoundingClientRect()
        const links = Array.from(navigationList.value.querySelectorAll<HTMLAnchorElement>('a'))

        // Find first link that's cut off on the right
        for (const link of links) {
            if (!link) continue

            const linkRect = link.getBoundingClientRect()
            if (linkRect.right > navRect.right + 1) {
                link.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' })
                break
            }
        }
    }

    return {
        navigationWrapper,
        navigationList,
        sentinelStart,
        sentinelEnd,

        showLeftArrow,
        showRightArrow,

        handleScrollLeft,
        handleScrollRight
    }
}
